import { VALIDATION_PATTERNS } from './constants';

// Date formatting helpers
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateString);
  }
};

// Validation helpers
export const validateTag = (tag) => {
  return VALIDATION_PATTERNS.tag.test(tag);
};

export const validateEmail = (email) => {
  return VALIDATION_PATTERNS.email.test(email);
};

// Text processing helpers
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const highlightText = (text, searchQuery) => {
  if (!searchQuery || !text) return text;
  
  const regex = new RegExp(`(${searchQuery})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

// Language detection helpers
export const detectLanguage = (content) => {
  const trimmedContent = content.trim().toLowerCase();
  
  // Check for common language patterns
  const patterns = {
    python: [/def\s+\w+\s*\(/, /import\s+\w+/, /from\s+\w+\s+import/, /print\s*\(/, /__init__/],
    javascript: [/function\s+\w+\s*\(/, /const\s+\w+\s*=/, /let\s+\w+\s*=/, /var\s+\w+\s*=/, /console\.log/],
    java: [/public\s+class\s+\w+/, /import\s+java\./, /public\s+static\s+void\s+main/, /System\.out\.println/],
    cpp: [/#include\s*</, /int\s+main\s*\(/, /std::/, /cout\s*<</, /cin\s*>>/],
    html: [/<html>/i, /<!DOCTYPE/i, /<body>/i, /<div>/i, /<p>/i],
    css: [/\{\s*[\w-]+\s*:/, /\.[\w-]+\s*\{/, /#[\w-]+\s*\{/, /@media/],
    sql: [/SELECT\s+/i, /FROM\s+/i, /WHERE\s+/i, /INSERT\s+INTO/i, /UPDATE\s+/i],
    json: [/^\s*\{/, /^\s*\[/, /"[\w-]+"\s*:/, /null/, /true|false/],
    markdown: [/^#\s+/, /^##\s+/, /\*\*.*\*\*/, /\[.*\]\(.*\)/, /^-\s+/, /^1\.\s+/]
  };
  
  for (const [language, regexes] of Object.entries(patterns)) {
    if (regexes.some(regex => regex.test(trimmedContent))) {
      return language;
    }
  }
  
  return 'text';
};

// Color helpers
export const getLanguageColor = (language) => {
  const colors = {
    javascript: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    python: 'bg-blue-100 text-blue-800 border-blue-200',
    java: 'bg-red-100 text-red-800 border-red-200',
    cpp: 'bg-purple-100 text-purple-800 border-purple-200',
    html: 'bg-orange-100 text-orange-800 border-orange-200',
    css: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    json: 'bg-gray-100 text-gray-800 border-gray-200',
    markdown: 'bg-green-100 text-green-800 border-green-200',
    sql: 'bg-pink-100 text-pink-800 border-pink-200',
    text: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return colors[language] || colors.text;
};

// Copy to clipboard helper
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// Local storage helpers
export const getFromStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

export const setToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('Error writing to localStorage:', error);
    return false;
  }
};

export const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error removing from localStorage:', error);
    return false;
  }
};

// Theme helpers
export const applyTheme = (theme) => {
  const root = document.documentElement;
  
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // Auto theme - detect system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

// Debounce helper
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Throttle helper
export const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Generate random ID
export const generateId = () => {
  return Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
};

// Sort helpers
export const sortByDate = (items, key = 'updatedAt', order = 'desc') => {
  return [...items].sort((a, b) => {
    const dateA = new Date(a[key]);
    const dateB = new Date(b[key]);
    return order === 'desc' ? dateB - dateA : dateA - dateB;
  });
};

export const sortByUsage = (items, key = 'usageCount', order = 'desc') => {
  return [...items].sort((a, b) => {
    const countA = a[key] || 0;
    const countB = b[key] || 0;
    return order === 'desc' ? countB - countA : countA - countB;
  });
};
